<template>
    <div class="p-2 card-bg ml-10">
        <DataTable :value="publisherData" :loading="loading"  :scrollable="true" scrollHeight="400px" :contextMenu="true"        @contextmenu="onRowRightClick"
            :contextMenuSelection="selectedRow"
            @row-contextmenu="onRowRightClick">
            <Column header="Advertiser" :headerStyle="headerStyles">
                <template #body="{ data }">
                    {{ data.advertiser.name }}
                    <span :class="`fi fi-${data.market.toLowerCase()}`" class="ml-2"></span>
                </template>
            </Column>
            <!-- <Column field="internalNote" header="Ext Key" :headerStyle="headerStyles">
            </Column> -->
            <Column field="metrics.clicks" header="Clicks" sortable  :headerStyle="headerStyles">
            </Column>
            <Column field="metrics.uniqueClicks" header="UC's" sortable 
                :headerStyle="headerStyles">
            </Column>
            <Column field="metrics.signups" header="SU's" sortable  :headerStyle="headerStyles">
            </Column>
            <Column field="metrics.FTD" header="FTDs" sortable  :headerStyle="headerStyles">
            </Column>
            <Column field="metrics.deposits" header="D's" sortable 
                :headerStyle="headerStyles">
            </Column>
            <Column header="D value" sortable  :headerStyle="headerStyles">
                <template #body="slotProps">
                    <div>
                        <!-- Call the formatCurrency function, now it always formats in Euro -->
                        <p>{{ formatCurrency(slotProps.data.metrics.depositValue) }}</p> <!-- Output: €6,031.56 -->
                    </div>
                </template>
            </Column>
            <Column field="metrics.CPA" header="CPAs" sortable  :headerStyle="headerStyles">
            </Column>
            <Column header="Casino GGR" :headerStyle="headerStyles">
                <template #body="{ data }">
                    <p>{{ formatCurrency(data.metrics.casinoGGR) }}</p>
                </template>
            </Column>
            <Column header="Pub Deal" :headerStyle="headerStyles">
                <template #body="{ data }">
                    {{ getFriendlyDealMessage(data.customerDeal) }}
                </template>
            </Column>
            <Column header="Pub rev" sortable  :headerStyle="headerStyles">
                <template #body="{ data }">
                    <div>
                        <!-- Call the formatCurrency function, now it always formats in Euro -->
                        <p>{{ formatCurrency(getTotalEarnings(data.metrics.customerCPRevenue,
                            data.metrics.customerRevshareRevenue)) }}
                        </p>
                        <!-- Output: €6,031.56 -->
                    </div>
                </template>
            </Column>
            <Column header="Our Deal" :headerStyle="headerStyles">
                <template #body="{ data }">
                    {{ getFriendlyDealMessage(data.ourDeal) }}
                </template>
            </Column>
            <Column header="Our rev" sortable  :headerStyle="headerStyles">
                <template #body="{ data }">
                    <div
                        :class="{ 'income-cell': getTotalEarnings(data.metrics.ourCPRevenue, data.metrics.ourRevshareRevenue) > 0 }">
                        <!-- Call the formatCurrency function, now it always formats in Euro -->
                        <p>{{ formatCurrency(getTotalEarnings(data.metrics.ourCPRevenue,
                            data.metrics.ourRevshareRevenue)) }}</p>
                        <!-- Output: €6,031.56 -->
                    </div>
                </template>
            </Column>
            
            <!-- Phones have no right-click, and iOS never fires contextmenu on a
                 long-press, so the same row menu also opens from this button.
                 Frozen right so it stays reachable when the table scrolls sideways. -->
            <Column frozen alignFrozen="right" :headerStyle="headerStyles" style="width: 3rem">
                <template #body="{ data }">
                    <Button icon="pi pi-ellipsis-v" text rounded size="small"
                        aria-label="Row actions" @click="openRowMenu($event, data)" />
                </template>
            </Column>
        </DataTable>
        <Toast />
        <ContextMenu ref="contextMenu" :model="contextMenuItems" />
        <Adjustment 
            v-model:visible="showAdjustmentModal" 
            :rowData="selectedRow" 
            @savedAdjustment="handleSavedAdjustment"
        />
    </div>
</template>

<script setup>
import { ref, onMounted, shallowRef } from 'vue';
import EarningsService from '@/services/EarningsService';
import { getFriendlyDealMessage } from '@/utils/deals';
import { formatCurrency } from '@/utils/currencies';
import { headerStyles } from '@/global/headerStyles';
import { getTotalEarnings } from '@/utils/earnings';
import Adjustment from './Adjustment.vue';
import Button from 'primevue/button';
import Toast from 'primevue/toast';
import { useToast } from 'primevue/usetoast';

const props = defineProps({
    publisher: {
        type: Object,
        required: true
    },
    from: {
        type: Date,
        required: true
    },
    to: {
        type: Date,
        required: true
    }
});

const emit = defineEmits(['adjustmentSaved']);
const toast = useToast();

const loading = ref(true);
const publisherData = shallowRef([]);
const contextMenu = ref(null);
const selectedRow = ref(null);
const showAdjustmentModal = ref(false);
// Define context menu items
const contextMenuItems = ref([
    {
        label: 'Make an Adjustment',
        icon: 'pi pi-eye',
        command: () => {
            if (selectedRow.value) {
                console.log('selectedRow', selectedRow.value);
                showAdjustmentModal.value = true;
            }
        }
    },
    {
        label: 'Go to Advertiser',
        icon: 'pi pi-external-link',
        command: () => goToAdvertiser(),
    },
    {
        label: 'Copy Advertiser Name',
        icon: 'pi pi-copy',
        command: () => {
            if (selectedRow.value) {
                navigator.clipboard.writeText(selectedRow.value.advertiser.name);
                // Optionally show a toast notification
            }
        }
    }
]);

/**
 * Open the affiliate system's login page for the row that was right-clicked.
 *
 * The earnings payload doesn't carry loginUrl - it lives on the site link's
 * spaceAdvertiserLogin - so it's fetched on demand. The row is still checked
 * first, so this keeps working for free if the field is ever denormalised into
 * the earnings response.
 *
 * The blank tab is opened SYNCHRONOUSLY, before the await. Browsers only honour
 * window.open inside a user gesture, and awaiting the fetch first ends that
 * gesture - the popup blocker would swallow the tab. Opening first and setting
 * location afterwards keeps it attributed to the click.
 */
// A bare domain would otherwise resolve against our own host.
const absoluteUrl = url => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

const goToAdvertiser = async () => {
    const row = selectedRow.value;
    if (!row) return;

    const known = row.loginUrl || row.spaceAdvertiserLogin?.loginUrl || null;

    // Already have it: open directly, and use noopener since we don't need the
    // handle back.
    if (known) {
        window.open(absoluteUrl(known), '_blank', 'noopener,noreferrer');
        return;
    }

    // Deliberately NO 'noopener' here: with it, window.open returns null by
    // spec, and we need the handle to navigate the tab once the fetch resolves.
    // opener is severed manually below instead, which gets the same protection.
    const tab = window.open('', '_blank');
    if (!tab) {
        toast.add({
            severity: 'warn',
            summary: 'Popup blocked',
            detail: 'Allow popups for this site to open the advertiser login.',
            life: 4000,
        });
        return;
    }

    try {
        const { data } = await EarningsService.getBySiteBrandLink(row.id);
        const url = data?.siteBrandLink?.spaceAdvertiserLoginId?.loginUrl;

        if (!url) {
            tab.close();
            toast.add({
                severity: 'warn',
                summary: 'No login URL',
                detail: `${row.advertiser?.name || 'This advertiser'} has no login URL saved on its affiliate system.`,
                life: 4000,
            });
            return;
        }

        tab.location = absoluteUrl(url);
        // Sever the back-reference so the opened page can't touch window.opener.
        try { tab.opener = null; } catch { /* cross-origin once navigated */ }
    } catch (error) {
        console.error('Error resolving advertiser login URL:', error);
        tab.close();
        toast.add({
            severity: 'error',
            summary: 'Could not open advertiser',
            detail: 'Failed to look up the login URL. Please try again.',
            life: 4000,
        });
    }
};

const handleSavedAdjustment = () => {
    showAdjustmentModal.value = false;
    // Refresh this breakdown...
    fetchData();
    // ...and tell the parent to refresh the publisher summary row above it.
    // The summary comes from props.publisher, which this component does not own,
    // so refetching only our own data left the row overhead showing pre-adjustment
    // totals while the rows beneath it had already moved.
    emit('adjustmentSaved');
};
const onRowRightClick = (event) => {
    console.log('onRowRightClick', event);
    // Prevent default browser context menu
    event.originalEvent.preventDefault();
    
    // Get the row data from the event
    selectedRow.value = event.data;
    
    // Show the context menu at the cursor position
    contextMenu.value.show(event.originalEvent);
};
// Touch screens can't right-click: the row's ⋮ button opens the same menu.
const openRowMenu = (event, row) => {
    selectedRow.value = row;
    contextMenu.value.show(event);
};
const fetchData = async () => {
    try {
        loading.value = true;
        const response = await EarningsService.getAdvertiserEarningsByPublisher(props.from, props.to, props.publisher.publisher.id);
        console.log('Data fetched:', response.data);
        publisherData.value = response.data;
        // Handle the data as needed
    } catch (error) {
        console.error('Error fetching data:', error);
    } finally {
        loading.value = false;
    }
};

onMounted(() => {
    fetchData();
});
</script>